"use client";

import { useMemo } from "react";
import type { Tick } from "@/lib/types";

interface Props {
  ticks: Tick[];
  height?: number;
}

export function PriceChart({ ticks, height = 120 }: Props) {
  const chart = useMemo(() => buildChart(ticks, height), [ticks, height]);
  if (!chart) {
    return (
      <div
        className="flex items-center justify-center text-xs text-zinc-600 border border-zinc-800 rounded"
        style={{ height }}
      >
        樣本不足，至少需要 2 個資料點
      </div>
    );
  }
  return (
    <div className="relative w-full border border-zinc-800 rounded bg-zinc-900/40">
      <svg
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height }}
      >
        {/* horizontal grid */}
        {chart.gridY.map((g, i) => (
          <line
            key={`g${i}`}
            x1={0}
            x2={chart.width}
            y1={g.y}
            y2={g.y}
            stroke="#27272a"
            strokeDasharray="2 3"
          />
        ))}
        {/* area under line */}
        <path d={chart.areaPath} fill={chart.pctChange >= 0 ? "#10b98128" : "#f4364628"} />
        {/* price line */}
        <path
          d={chart.linePath}
          fill="none"
          stroke={chart.pctChange >= 0 ? "#34d399" : "#fb7185"}
          strokeWidth={1.5}
        />
        {/* last-point marker */}
        <circle
          cx={chart.last.x}
          cy={chart.last.y}
          r={3}
          fill={chart.pctChange >= 0 ? "#34d399" : "#fb7185"}
        />
      </svg>

      <div className="absolute top-1 left-2 text-[10px] text-zinc-500 tabular-nums flex gap-3">
        <span>High ${chart.highStr}</span>
        <span>Low ${chart.lowStr}</span>
      </div>
      <div className="absolute top-1 right-2 text-[10px] tabular-nums">
        <span className={chart.pctChange >= 0 ? "text-emerald-400" : "text-rose-400"}>
          {chart.pctChange >= 0 ? "+" : ""}
          {chart.pctChange.toFixed(2)}%
        </span>
        <span className="text-zinc-600 ml-2">
          {chart.spanLabel} · {chart.sampleCount} 樣本
        </span>
      </div>
      <div className="absolute bottom-1 right-2 text-[10px] text-zinc-400 tabular-nums">
        現價 <span className="text-zinc-100">${chart.currentStr}</span>
      </div>
    </div>
  );
}

interface ChartData {
  width: number;
  height: number;
  linePath: string;
  areaPath: string;
  gridY: Array<{ y: number }>;
  last: { x: number; y: number };
  pctChange: number;
  currentStr: string;
  highStr: string;
  lowStr: string;
  spanLabel: string;
  sampleCount: number;
}

function buildChart(ticks: Tick[], height: number): ChartData | null {
  if (ticks.length < 2) return null;
  const sorted = [...ticks].sort((a, b) => a.ts - b.ts);
  const width = 600;
  const padding = 4;
  const prices = sorted.map((t) => t.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min || max * 0.01 || 1;
  const firstTs = sorted[0].ts;
  const lastTs = sorted[sorted.length - 1].ts;
  const tsSpan = lastTs - firstTs || 1;

  const points = sorted.map((t) => {
    const x = padding + ((t.ts - firstTs) / tsSpan) * (width - 2 * padding);
    const y =
      height - padding - ((t.price - min) / span) * (height - 2 * padding);
    return { x, y };
  });

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");

  const areaPath =
    linePath
    + ` L${points[points.length - 1].x.toFixed(2)},${height - padding}`
    + ` L${points[0].x.toFixed(2)},${height - padding} Z`;

  const pctChange = ((sorted[sorted.length - 1].price - sorted[0].price) / sorted[0].price) * 100;
  const minutes = Math.round(tsSpan / 60_000);
  const spanLabel = minutes >= 60 ? `${(minutes / 60).toFixed(1)}h` : `${minutes}m`;

  return {
    width,
    height,
    linePath,
    areaPath,
    gridY: [0.25, 0.5, 0.75].map((f) => ({ y: height * f })),
    last: points[points.length - 1],
    pctChange,
    currentStr: formatPrice(sorted[sorted.length - 1].price),
    highStr: formatPrice(max),
    lowStr: formatPrice(min),
    spanLabel,
    sampleCount: sorted.length,
  };
}

function formatPrice(p: number): string {
  if (p >= 1000) return p.toFixed(2);
  if (p >= 1) return p.toPrecision(5);
  if (p >= 0.01) return p.toPrecision(4);
  return p.toPrecision(3);
}
